import type {AtsEntity} from 'src/types/ats-entities';
import type {
  Sort,
  UniqueKeyPayload,
  CreateListPayload,
  ListFilterPayload,
  CSVListTableQuery,
  UniqueKeyPayloadAPI,
  CSVListSearchPayload,
  LeadEntitiesUniqueKey,
  CSVListUploadHistoryQuery,
  LeadEntityAttributePayload,
} from 'src/types/audience-csv-list';
import type {Dispatch, ThunkAction} from 'src/reducers';
import FileSaver from 'file-saver';
import * as reduxApi from 'src/utils/redux-api';
import * as reduxApiV2 from 'src/utils/redux-api-v2';
import * as reduxApiBff from 'src/utils/redux-api-bff';


export const RECEIVE_CSV_LISTS = 'audience-csv-list/receive-csv-lists';
export const RECEIVE_UNIQUE_KEY = 'audience-csv-list/receive-unique-key';
export const RECEIVE_LEAD_ENTITIES = 'audience-csv-list/receive-lead-entities';
export const RECEIVE_CSV_LIST_SORT = 'audience-csv-list/receive-csv-list-sort';
export const RECEIVE_CSV_LIST_QUERY =
  'audience-csv-list/receive-csv-list-query';
export const RECEIVE_CSV_LIST_FILTER =
  'audience-csv-list/receive-csv-list-filter';
export const RECEIVE_CSV_LIST_REFETCH =
  'audience-csv-list/receive-csv-list-refetch';
export const RECEIVE_ENTITY_ATTRIBUTES =
  'audience-csv-list/receive-entity-attributes';
export const RECEIVE_ENTITIES_UNIQUE_KEY =
  'audience-csv-list/receive-entities-unique-key';
export const RECEIVE_CSV_LIST_PAGE_NUMBER =
  'audience-csv-list/receive-csv-list-page-number';

type ReceiveCSVListsAction = {
  type: 'audience-csv-list/receive-csv-lists';
  payload: CSVListSearchPayload;
};

type ReceiveUniqueKeyAction = {
  type: 'audience-csv-list/receive-unique-key';
  payload: UniqueKeyPayloadAPI;
};

type ReceiveLeadEntitiesAction = {
  type: 'audience-csv-list/receive-lead-entities';
  payload: Array<AtsEntity>;
};

type ReceiveCSVListSortAction = {
  type: 'audience-csv-list/receive-csv-list-sort';
  payload: Sort;
};

type ReceiveCSVListQueryAction = {
  type: 'audience-csv-list/receive-csv-list-query';
  payload: string;
};

type ReceiveCSVListFilterAction = {
  type: 'audience-csv-list/receive-csv-list-filter';
  payload: ListFilterPayload;
};

type ReceiveCSVListRefetchAction = {
  type: 'audience-csv-list/receive-csv-list-refetch';
};

type ReceiveEntityAttributesAction = {
  type: 'audience-csv-list/receive-entity-attributes';
  payload: LeadEntityAttributePayload;
};

type ReceiveEntitiesUniqueKeyAction = {
  type: 'audience-csv-list/receive-entities-unique-key';
  payload: LeadEntitiesUniqueKey;
};

type ReceiveCSVListPageNumberAction = {
  type: 'audience-csv-list/receive-csv-list-page-number';
  payload: number;
};

export type AudienceCSVListActions =
  | ReceiveCSVListsAction
  | ReceiveUniqueKeyAction
  | ReceiveCSVListSortAction
  | ReceiveLeadEntitiesAction
  | ReceiveCSVListQueryAction
  | ReceiveCSVListFilterAction
  | ReceiveCSVListRefetchAction
  | ReceiveEntityAttributesAction
  | ReceiveEntitiesUniqueKeyAction
  | ReceiveCSVListPageNumberAction;

export const receiveCSVLists = (
  payload: CSVListSearchPayload,
): ReceiveCSVListsAction => ({
  type: RECEIVE_CSV_LISTS,
  payload,
});

export const receiveUniqueKey = (
  payload: UniqueKeyPayloadAPI,
): ReceiveUniqueKeyAction => ({
  type: RECEIVE_UNIQUE_KEY,
  payload,
});

export const receiveLeadEntities = (
  payload: Array<AtsEntity>,
): ReceiveLeadEntitiesAction => ({
  type: RECEIVE_LEAD_ENTITIES,
  payload,
});

export const receiveCSVListSort = (
  payload: Sort,
): ReceiveCSVListSortAction => ({
  type: RECEIVE_CSV_LIST_SORT,
  payload,
});

export const receiveCSVListQuery = (
  payload: string,
): ReceiveCSVListQueryAction => ({
  type: RECEIVE_CSV_LIST_QUERY,
  payload,
});

export const receiveCSVListFilter = (
  payload: ListFilterPayload,
): ReceiveCSVListFilterAction => ({
  type: RECEIVE_CSV_LIST_FILTER,
  payload,
});

export const refetchCSVList = (): ReceiveCSVListRefetchAction => ({
  type: RECEIVE_CSV_LIST_REFETCH,
});

export const receiveLeadEntityAttributes = (
  payload: LeadEntityAttributePayload,
): ReceiveEntityAttributesAction => ({
  type: RECEIVE_ENTITY_ATTRIBUTES,
  payload,
});

export const receiveEntitiesUniqueKey = (
  payload: LeadEntitiesUniqueKey,
): ReceiveEntitiesUniqueKeyAction => ({
  type: RECEIVE_ENTITIES_UNIQUE_KEY,
  payload,
});

export const receiveCSVListPageNumber = (
  payload: number,
): ReceiveCSVListPageNumberAction => ({
  type: RECEIVE_CSV_LIST_PAGE_NUMBER,
  payload,
});

export const getLeadEntities: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApiV2.get('entities/lead-entity-types'),
      );
      dispatch(receiveLeadEntities(response));
    } catch (error: any) {
      dispatch(receiveLeadEntities([]));
      console.error(error);
    }
  };

export const getLeadEntityAttributes: (
  entityType: string,
) => ThunkAction<unknown> =
  (entityType: string) => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApiBff.get(`entity_attribute/${entityType}`),
      );
      dispatch(receiveLeadEntityAttributes({entityType, attributes: response}));
    } catch (error: any) {
      dispatch(receiveLeadEntityAttributes({entityType, attributes: []}));
      console.error(error);
    }
  };

export const getLeadEntitiesUniqueKey: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const {entity_unique_key} = await dispatch(
        reduxApiV2.get('entities/lead-entity-unique-key'),
      );
      dispatch(receiveEntitiesUniqueKey(entity_unique_key));
    } catch (error: any) {
      dispatch(receiveEntitiesUniqueKey({}));
      console.error(error);
    }
  };

export const setLeadEntityUniqueKey: (
  payload: UniqueKeyPayload,
) => ThunkAction<unknown> =
  (payload: UniqueKeyPayload) => async (dispatch: Dispatch) => {
    const {entityType, uniqueKey} = payload;
    try {
      const response = await dispatch(
        reduxApiV2.post('entities/lead-entity-unique-key', {
          entity_type: entityType,
          unique_key: uniqueKey,
        }),
      );
      dispatch(receiveUniqueKey(response));
    } catch (error: any) {
      console.error(error);
    }
  };

export const getSampleCSV: (entityType: string) => ThunkAction<unknown> =
  (entityType: string) => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.base(`csv/sample-template/${entityType}`, {
          options: {
            apiPath: '/bff/api/v1/',
          },
        }),
      );
      if (response.ok && typeof File != null) {
        let blob = await response.blob();
        blob = new File([blob], `${entityType}-sample-csv.csv`);
        FileSaver.saveAs(blob);
      } else {
        throw new Error('Unable to save a file.');
      }
    } catch (error: any) {
      console.error(error);
    }
  };

export const getErrorReport: (url: string) => ThunkAction<unknown> =
  (url: string) => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.base(`csv/report/${url}`, {
          options: {
            apiPath: '/bff/api/v1/',
          },
        }),
      );
      if (response.ok && typeof File != null) {
        let blob = await response.blob();
        blob = new File([blob], 'error-report.csv');
        FileSaver.saveAs(blob);
      } else {
        throw new Error('Unable to save a file.');
      }
    } catch (error: any) {
      console.error(error);
    }
  };

export const createCSVList: (
  file: File,
  listDetails: CreateListPayload,
) => ThunkAction<string> =
  (file: File, listDetails: CreateListPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const {csvId} = await dispatch(
        reduxApiBff.post('csv/upload', {file}, {}, {multipart: true}),
      );
      const filter = {
        ...listDetails,
        internal: false,
        source: 'CSV_LIST_UI',
        sourceId: csvId,
      } as const;
      const {id: listID} = await dispatch(reduxApiBff.post('filter', filter));
      return listID;
    } catch (error: any) {
      console.error(error);
      throw new Error('List submission error.');
    }
  };

export const getCSVLists: (
  query: CSVListTableQuery,
  isNew?: boolean,
) => ThunkAction<unknown> =
  (query: CSVListTableQuery, isNew: boolean = false) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(reduxApiBff.get('filter/search', query));
      const {filters, totalCount, totalPages} = response;
      dispatch(
        receiveCSVLists({
          isNew,
          lists: filters || [],
          pageNo: query.pageNo + 1,
          totalLists: totalCount || 0,
          totalPages: totalPages || 0,
        }),
      );
    } catch (error: any) {
      dispatch(
        receiveCSVLists({
          isNew: true,
          lists: [],
          pageNo: 1,
          totalLists: 0,
          totalPages: 1,
        }),
      );
      console.error(error);
    }
  };

export const getUploadHistoryData: (
  query: CSVListUploadHistoryQuery,
) => ThunkAction<unknown> =
  (query: CSVListUploadHistoryQuery) => (dispatch: Dispatch) =>
    dispatch(reduxApiBff.get('filter/csv-upload-history', query));

export const deleteCSVLists: (listIDs: Array<number>) => ThunkAction<unknown> =
  (listIDs: Array<number>) => (dispatch: Dispatch) =>
    dispatch(reduxApiBff.del('filter', {ids: listIDs}));

export const archiveCSVLists: (
  listIDs: Array<number>,
) => ThunkAction<unknown> = (listIDs: Array<number>) => (dispatch: Dispatch) =>
  dispatch(reduxApiBff.patch('filter/archive', {ids: listIDs}));

export const unarchiveCSVLists: (
  listIDs: Array<number>,
) => ThunkAction<unknown> = (listIDs: Array<number>) => (dispatch: Dispatch) =>
  dispatch(reduxApiBff.patch('filter/unarchive', {ids: listIDs}));

export const updateCSVList: (
  listID: string,
  name: string,
) => ThunkAction<unknown> =
  (listID: string, name: string) => (dispatch: Dispatch) =>
    dispatch(reduxApiBff.patch(`filter/${listID}`, {id: listID, name}));
